<template>
    <span>
        <!-- Icon variant (dashboard) -->
        <button
            v-if="variant === 'icon'"
            class="task-meta-link border-0 bg-transparent p-0"
            :class="{
                'text-success': status === 'done' && !removeUrl,
                'text-warning':  status === 'done' && removeUrl,
                'text-danger':  status === 'error',
                'opacity-50':   dirty
            }"
            :disabled="dirty || status === 'loading' || status === 'removing'"
            :title="iconTitle"
            :aria-label="iconTitle"
            @click="handleClick"
        >
            <i
                class="fa"
                :class="{
                    'fa-calendar-plus-o': status === 'idle',
                    'fa-spinner fa-spin': status === 'loading' || status === 'removing',
                    'fa-calendar-times-o': status === 'done' && removeUrl,
                    'fa-check':           status === 'done' && !removeUrl,
                    'fa-times':           status === 'error',
                }"
                aria-hidden="true"
            ></i>
        </button>

        <!-- Material variant - "Unlink" state (event linked, or removal in progress) -->
        <a
            v-else-if="(status === 'done' || status === 'removing') && removeUrl"
            href="#!"
            class="waves-effect btn"
            :class="dirty ? 'grey lighten-1 disabled' : (status === 'removing' ? 'grey' : 'orange darken-1')"
            :title="dirty ? 'Save your deadline changes first' : 'Unlink from Google Calendar'"
            aria-label="Unlink from Google Calendar"
            @click.prevent="handleClick"
        >
            <i class="material-icons left" aria-hidden="true">{{ status === 'removing' ? 'hourglass_empty' : 'event_busy' }}</i>
            <span class="hide-on-small-only">{{ status === 'removing' ? 'Unlinking…' : 'Unlink from calendar' }}</span>
        </a>

        <!-- Material variant - "Add" state -->
        <a
            v-else
            href="#!"
            class="waves-effect btn"
            :class="dirty ? 'grey lighten-1 disabled' : {
                'blue darken-1': status === 'idle',
                'grey':          status === 'loading',
                'green':         status === 'done',
                'red':           status === 'error',
            }"
            :title="dirty ? 'Save your deadline changes first' : (status === 'error' ? errorMessage : (status === 'done' ? 'Added to Google Calendar' : 'Add to Google Calendar'))"
            :aria-label="dirty ? 'Save your deadline changes first' : (status === 'done' ? 'Added to Google Calendar' : 'Add to Google Calendar')"
            @click.prevent="handleClick"
        >
            <i class="material-icons left" aria-hidden="true">{{ dirty ? 'event_note' : materialIcon }}</i>
            <span class="hide-on-small-only">{{ dirty ? 'Add to calendar' : materialLabel }}</span>
        </a>
    </span>
</template>

<script>
import axios from 'axios';

export default {
    name: 'AddToCalendarButton',
    props: {
        url: {
            type: String,
            required: true
        },
        removeUrl: {
            type: String,
            default: null
        },
        added: {
            type: Boolean,
            default: false
        },
        variant: {
            type: String,
            default: 'icon'
        },
        dirty: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            status: this.added ? 'done' : 'idle',
            errorMessage: ''
        };
    },
    watch: {
        added(newVal) {
            if (this.status !== 'loading' && this.status !== 'removing') {
                this.status = newVal ? 'done' : 'idle';
                this.errorMessage = '';
            }
        }
    },
    computed: {
        iconTitle() {
            if (this.dirty) return 'Save your deadline changes first';
            if (this.status === 'error') return this.errorMessage;
            if (this.status === 'done') return this.removeUrl ? 'Unlink from Google Calendar' : 'Added to Google Calendar';
            return 'Add to Google Calendar';
        },
        materialIcon() {
            switch (this.status) {
                case 'loading': return 'hourglass_empty';
                case 'done':    return 'event_available';
                case 'error':   return 'event_busy';
                default:        return 'event_note';
            }
        },
        materialLabel() {
            switch (this.status) {
                case 'loading': return 'Adding…';
                case 'done':    return 'Added to calendar';
                case 'error':   return 'Failed — retry?';
                default:        return 'Add to calendar';
            }
        }
    },
    methods: {
        handleClick() {
            if (this.dirty) return;
            if (this.status === 'done' && this.removeUrl) {
                this.removeFromCalendar();
            } else if (this.status !== 'loading' && this.status !== 'done' && this.status !== 'removing') {
                this.addToCalendar();
            }
        },
        async addToCalendar() {
            this.status = 'loading';
            try {
                const response = await axios.post(this.url);
                this.status = 'done';
                this.$emit('added', response.data.event_id || null);
            } catch (err) {
                this.errorMessage = err.response?.data?.error || 'Failed to add to Google Calendar.';
                this.status = 'error';
            }
        },
        async removeFromCalendar() {
            this.status = 'removing';
            try {
                await axios.delete(this.removeUrl);
                this.status = 'idle';
                this.$emit('removed');
            } catch (err) {
                this.errorMessage = err.response?.data?.error || 'Failed to remove from Google Calendar.';
                this.status = 'error';
            }
        }
    }
};
</script>
